import React, { useState } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
// import Select from 'react-select';
import { Select } from 'src/components';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { UserListHead } from 'src/sections/@dashboard/user';
import CustomPagination from 'src/components/CustomPagination';
import { DeleteIcon, UserIcon, TransactionIcon, EyeIcon } from 'src/images';
import palette from 'src/theme/palette';
import { teacherService } from 'src/services/teacher.service';

import './../../styles/users.css';
import 'react-toastify/dist/ReactToastify.css';
import { IoClose } from 'react-icons/io5';
import { AiFillQuestionCircle } from 'react-icons/ai';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const TABLE_HEAD = [
  { id: 'name', label: 'Teacher Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  // { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'teacher-id', label: 'Teacher ID', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

interface SubjectOption {
  value: string;
  label: string;
}

const subjectOptions: SubjectOption[] = [
  { value: 'Mathematics', label: 'Mathematics' },
  { value: 'Science', label: 'Science' },
  { value: 'English', label: 'English' },
  { value: 'History', label: 'History' },
  { value: 'Geography', label: 'Geography' },
];

export default function Teachers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Queries
  const {
    data: teacherData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['teachers', { page, rowsPerPage, searchText, subject: selectedSubject }],
    queryFn: () =>
      teacherService.getTeachers({
        page,
        limit: rowsPerPage,
        search: searchText,
        subject: selectedSubject,
      }),
  });

  const handleClose = () => setOpen(false);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => teacherService.deleteTeacher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
      setOpen(false);
      setSelectedTeacherId(null);
      toast.success('Teacher deleted successfully');
    },
    onError: (error) => {
      console.error('Delete teacher error:', error);
      toast.error('Failed to delete teacher');
    },
  });

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (pageNumber: number) => {
    setRowsPerPage(pageNumber);
    setPage(1);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedTeacherId(id);
    setOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedTeacherId) {
      deleteMutation.mutate(selectedTeacherId);
    }
  };

  if (error) {
    toast.error('Failed to fetch teachers');
    return <Typography>Error loading teachers</Typography>;
  }

  return (
    <Page title="Teachers">
      <Container maxWidth={false} sx={{ px: 1 }}>
        <Card style={{ borderRadius: 12, boxShadow: 'none' }}>
          <Grid container spacing={2} style={{ padding: 20 }}>
            <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
              <div
                className="_login_row search_container"
                style={{ backgroundColor: palette.grey[400], borderRadius: 12 }}
              >
                <CiSearch size={24} color={palette.text.primary} />
                <input
                  type="text"
                  placeholder="Search by Name, Email or ID"
                  className="_auth_fields searchField"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </Grid>
            <Grid item sm={12} xs={12} md={6} lg={4} xl={4}>
              <Select
                options={subjectOptions}
                onChange={(option) => setSelectedSubject(option)}
                value={selectedSubject}
                placeholder="Grade"
                firstItem="All Grades"
              />
            </Grid>
            <Grid item sm={12} xs={12} md={4} lg={2} xl={2}>
              <Button
                variant="contained"
                style={{ height: 55 }}
                fullWidth
                onClick={() => navigate('/admin/add-teacher')}
              >
                Add New Teacher
              </Button>
            </Grid>
          </Grid>

          <Scrollbar>
            <Table>
              <UserListHead headLabel={TABLE_HEAD} />
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : teacherData?.total ? (
                  teacherData.data.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => navigate(`/admin/view-teacher/${row.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' } }}
                    >
                      <TableCell>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ '&:hover': { color: '#2563EB', textDecoration: 'underline' } }}
                        >
                          {`${row.user.firstname} ${row.user.lastname}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.user.extendedproperties?.teacherId || 'N/A'}</Typography>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            onClick={() => navigate(`/admin/view-teacher/${row.id}`)}
                            title="View Educator Profile"
                          >
                            <img src={EyeIcon} alt="View Profile" style={{ width: '20px', height: '20px' }} />
                          </IconButton>
                          <IconButton
                            onClick={() => navigate(`/admin/edit-teacher/${row.id}`)}
                            title="Edit Account"
                          >
                            <img src={UserIcon} alt="Edit Icon" style={{ width: '20px', height: '20px' }} />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteClick(row.user.id)} title="Delete Educator">
                            <img src={DeleteIcon} alt="Delete Icon" style={{ width: '20px', height: '20px' }} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : null}

                {teacherData && teacherData.total > 0 && (
                  <TableRow style={{ height: 53 }}>
                    <TableCell colSpan={5} />
                  </TableRow>
                )}
              </TableBody>

              {teacherData && teacherData.total === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={5}>
                      <SearchNotFound searchQuery={searchText} sx={{ boxShadow: 'none', py: 5 }} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </Scrollbar>

          {teacherData && (
            <CustomPagination
              count={teacherData.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <BootstrapDialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              borderRadius: 16,
              padding: '16px',
              maxWidth: '400px',
              width: '100%',
              disple: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          <IoClose
            color={palette.grey[500]}
            size={24}
            style={{ cursor: 'pointer', alignSelf: 'flex-end' }}
            onClick={() => setOpen(false)}
          />
          <AiFillQuestionCircle size={120} color={palette.warning.main} />

          <DialogTitle
            sx={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#111827',
              pb: 1,
              textAlign: 'center',
            }}
          >
            Are You Sure?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#6B7280', fontSize: '1rem', textAlign: 'center' }}>
              You want to delete the Teacher?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: 'column',
              gap: 1,
              pt: 2,
              pb: 1,
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              sx={{
                height: 48,
                backgroundColor: '#F63D58',
                '&:hover': {
                  backgroundColor: '#E02B47',
                },
                width: '100%',
              }}
            >
              {deleteMutation.isPending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Yes, Delete'}
            </Button>
          </DialogActions>
        </BootstrapDialog>
      </Container>
    </Page>
  );
}
